import { useEffect, useState } from 'react';
import { listDreamRequests, markDreamRead, deleteDreamRequest } from '../adminApi';
import { toast } from 'sonner';
import { Star, Trash2, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

export default function DreamRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    listDreamRequests()
      .then(setRequests)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleRead = async (id) => {
    try {
      await markDreamRead(id);
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, is_read: true } : r)));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDreamRequest(id);
      setRequests((rs) => rs.filter((r) => r.id !== id));
      setDeleteId(null);
      toast.success('Talep silindi.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const unreadCount = requests.filter((r) => !r.is_read).length;

  if (loading) return <PageLoader />;
  if (error) return <PageError message={error} />;

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">Hayalim Talepleri</h1>
          <p className="text-white/35 text-sm mt-0.5">
            {requests.length} talep
            {unreadCount > 0 && (
              <span className="ml-2 text-[#c5a059]">{unreadCount} okunmamış</span>
            )}
          </p>
        </div>
      </div>

      {requests.length === 0 ? (
        <EmptyState icon={Star} text="Henüz talep yok." />
      ) : (
        <div className="space-y-2">
          {requests.map((req) => (
            <div
              key={req.id}
              className={`bg-[#111] border rounded-xl overflow-hidden transition-colors ${
                req.is_read ? 'border-white/[0.06]' : 'border-[#c5a059]/20'
              }`}
            >
              {/* Header row */}
              <div className="flex items-center gap-4 px-5 py-4">
                <div className="flex-shrink-0">
                  {req.is_read ? (
                    <CheckCircle size={15} className="text-white/20" />
                  ) : (
                    <Star size={15} className="text-[#c5a059]" />
                  )}
                </div>

                <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto] sm:grid-cols-12 gap-2 sm:gap-3 items-center">
                  <span className="sm:col-span-3 text-sm font-medium text-white truncate">
                    {req.name}
                  </span>
                  <span className="hidden sm:block sm:col-span-2 text-sm text-white/45 truncate">{req.phone}</span>
                  <span className="hidden sm:block sm:col-span-3 text-sm text-white/45 truncate">
                    {req.email || '—'}
                  </span>
                  <span className="hidden sm:block sm:col-span-2 text-xs text-white/40 truncate">
                    {req.project_type || '—'}
                  </span>
                  <span className="sm:col-span-2 text-xs text-white/25 text-right">
                    {req.created_at ? new Date(req.created_at).toLocaleDateString('tr-TR') : '—'}
                  </span>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {!req.is_read && (
                    <button
                      onClick={() => handleRead(req.id)}
                      title="Okundu işaretle"
                      className="p-1.5 rounded-lg text-white/35 hover:text-[#c5a059] hover:bg-[#c5a059]/10 transition-colors"
                    >
                      <CheckCircle size={14} />
                    </button>
                  )}

                  {deleteId === req.id ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleDelete(req.id)}
                        className="text-xs bg-red-500/15 text-red-400 border border-red-500/20 px-2.5 py-1 rounded-lg hover:bg-red-500/25 transition-colors"
                      >
                        Evet, sil
                      </button>
                      <button
                        onClick={() => setDeleteId(null)}
                        className="text-xs text-white/35 hover:text-white/60 px-2 py-1"
                      >
                        İptal
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteId(req.id)}
                      title="Sil"
                      className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}

                  <button
                    onClick={() => setExpanded(expanded === req.id ? null : req.id)}
                    className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.04] transition-colors"
                  >
                    {expanded === req.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>

              {/* Expanded body */}
              {expanded === req.id && (
                <div className="px-5 pb-5 border-t border-white/[0.05]">
                  <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    {req.budget_range && <Field label="Bütçe Aralığı" value={req.budget_range} />}
                    {req.preferred_location && (
                      <Field label="Tercih Edilen Konum" value={req.preferred_location} />
                    )}
                    {req.project_type && <Field label="Proje Tipi" value={req.project_type} />}
                  </div>
                  <div>
                    <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1.5">Mesaj</p>
                    <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">
                      {req.message}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm text-white/65">{value}</p>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="w-5 h-5 border-2 border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
    </div>
  );
}

function PageError({ message }) {
  return (
    <div className="p-8">
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4 text-red-400 text-sm">
        Hata: {message}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-white/20">
      <Icon size={32} className="mb-3" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
